import DoubleArrowIcon from '@mui/icons-material/DoubleArrow';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Container,
  Grid,
  Link,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';

import DynamicBreadcrumbs from '@/components/nav/Breadcrumb';
import ResourcesRightSidebar from '@/components/nav/ResourcesRightSidebar';
import AppLayout from '@/layouts/AppLayout';
import { Meta } from '@/layouts/Meta';

const FuelTax = () => {
  return (
    <AppLayout
      meta={
        <Meta
          title="International Fuel Tax Agreement (IFTA) 2025 | TaxZerone"
          description="Learn everything about IFTA, including registration, decals, filing, deadlines, and penalties for interstate truckers and fleet owners in 2025."
          keywords=""
          canonical="resources/excise-tax/international-fuel-tax-agreement/"
          ogType="article"
          // jsonLdPricing="11.99"
          // jsonLdAggregateRatingValue="4.95"
          // jsonLdAggregateRatingCount="4278"
        />
      }
    >
      <Container maxWidth="lg" sx={{ textAlign: 'center' }}>
        <Typography
          variant="h1"
          sx={{
            fontSize: { lg: 40, md: 36, sm: 32, xs: 24 },
            fontWeight: 600,
            mb: 3,
            mt: 5,
            textAlign: 'center',
            lineHeight: 1.2,
          }}
        >
          International Fuel Tax Agreement{' '}
          <span className="highlight-container">
            <span className="highlight">(IFTA) </span>
          </span>{' '}
        </Typography>
        <Typography
          variant="body1"
          sx={{
            fontSize: { lg: 20, md: 18, sm: 16, xs: 16 },
            fontWeight: 400,
            mt: 1,
            mb: 2,
          }}
        >
          Simplify Fuel Tax Reporting Across Jurisdictions!
        </Typography>
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Button
            variant="contained"
            className="btn-primary medium"
            sx={{
              fontSize: {
                xs: '16px !important',
                sm: '18px !important',
                lg: '24px !important',
              },
              transition: 'all 0.3s ease 0s',
              '&:hover, &:focus, &:active': { transform: 'scale(1.1)' },
            }}
            href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=ifta`}
          >
            E-file Now
          </Button>
        </Box>
      </Container>
      <Grid
        container
        sx={{
          backgroundColor: '#f0f2f9',
          py: 5,
          mx: '0 !important',
          width: '100% !important',
        }}
        spacing={2}
        m={0}
        mt={0}
      >
        <Container maxWidth="lg" sx={{ mb: 0 }}>
          <DynamicBreadcrumbs />
          <Grid
            container
            spacing={0}
            rowSpacing={0}
            m={0}
            sx={{ flexDirection: 'row-reverse' }}
          >
            <Grid
              size={{ xs: 12, md: 3.5, sm: 4.5 }}
              sx={{ display: { xs: 'none', sm: 'block' } }}
            >
              <ResourcesRightSidebar />
            </Grid>
            <Grid size={{ xs: 12, md: 8.5, sm: 7.5 }}>
              <Paper
                variant="outlined"
                sx={{
                  border: '1px solid #ccc',
                  p: 3,
                  px: 3,
                  textAlign: 'left',
                  mb: 0,
                }}
              >
                <Box
                  itemScope
                  itemType="https://schema.org/Article"
                  sx={{
                    p: 3,
                    px: 3,
                    backgroundColor: '#f0f5f8',
                    borderLeft: '5px solid #01456c',
                    mb: 4,
                    borderRadius: 3,
                    position: 'relative',
                  }}
                >
                  <link
                    itemProp="mainEntityOfPage"
                    href="https://www.taxzerone.com/resources/excise-tax/international-fuel-tax-agreement/"
                  />

                  {/* Article requires an image */}
                  <div
                    itemProp="image"
                    itemScope
                    itemType="https://schema.org/ImageObject"
                    style={{ display: 'none' }}
                  >
                    <link
                      itemProp="url"
                      href="https://www.taxzerone.com/assets/images/logo.webp"
                    />
                    <data itemProp="width" value="380" />
                    <data itemProp="height" value="124" />
                  </div>

                  <Box
                    sx={{
                      display: 'grid',
                      justifyContent: 'flex-start',
                      alignItems: 'center',
                      textAlign: 'left',
                      mb: 2,
                    }}
                  >
                    <Typography variant="body2">
                      Last updated:{' '}
                      <time
                        itemProp="datePublished"
                        dateTime="2026-08-10T09:00:00-05:00"
                      >
                        August 10, 2026
                      </time>
                      <time
                        itemProp="dateModified"
                        dateTime="2026-08-10T09:00:00-05:00"
                        style={{ display: 'none' }}
                      >
                        August 10, 2026
                      </time>
                    </Typography>

                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 600 }}
                      itemProp="author"
                      itemScope
                      itemType="https://schema.org/Person"
                    >
                      -by{' '}
                      <span itemProp="name">
                        <a
                          itemProp="url"
                          href="https://www.taxzerone.com/"
                          style={{ color: 'inherit', textDecoration: 'none' }}
                        >
                          Alexia Zepeda
                        </a>
                      </span>{' '}
                      | TaxZerone
                    </Typography>
                  </Box>

                  <Typography
                    variant="h4"
                    itemProp="headline"
                    sx={{
                      fontWeight: 600,
                      mb: 2,
                      fontSize: { md: 20, sm: 18, xs: 16 },
                      color: '#01456c',
                      fontStyle: 'italic',
                    }}
                  >
                    International Fuel Tax Agreement (IFTA) at a Glance
                  </Typography>

                  <Box
                    component="ul"
                    itemScope
                    itemType="https://schema.org/ItemList"
                    sx={{
                      mb: 0,
                      pl: 0,
                      listStyle: 'none',

                      '& li': {
                        mb: 1.6,
                        position: 'relative',
                        pl: 3,
                      },

                      '& li::before': {
                        content: '"★"',
                        position: 'absolute',
                        left: 0,
                        top: '-2px',
                        color: '#01456c',
                        fontSize: '18px',
                      },
                    }}
                  >
                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <data
                        itemProp="position"
                        value="1"
                        style={{ display: 'none' }}
                      />
                      <span itemProp="name">
                        <strong style={{ fontWeight: 600 }}>
                          {' '}
                          The International Fuel Tax Agreement (IFTA)
                        </strong>{' '}
                        is an agreement among participating U.S. states and
                        Canadian provinces that simplifies fuel tax reporting
                        for commercial carriers operating across multiple
                        jurisdictions.
                      </span>
                    </li>

                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <data
                        itemProp="position"
                        value="2"
                        style={{ display: 'none' }}
                      />
                      <span itemProp="name">
                        <strong style={{ fontWeight: 600 }}>
                          Who Must Register:
                        </strong>{' '}
                        Carriers operating qualified motor vehicles in{' '}
                        <strong style={{ fontWeight: 600 }}>
                          two or more IFTA jurisdictions
                        </strong>{' '}
                        whose base jurisdiction participates in IFTA.
                      </span>
                    </li>

                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <data
                        itemProp="position"
                        value="3"
                        style={{ display: 'none' }}
                      />
                      <span itemProp="name">
                        <strong style={{ fontWeight: 600 }}>
                          Qualified Motor Vehicle:
                        </strong>{' '}
                        Generally, a vehicle with{' '}
                        <strong style={{ fontWeight: 600 }}>
                          two axles and a gross vehicle weight over 26,000
                          pounds
                        </strong>
                        , a vehicle with{' '}
                        <strong style={{ fontWeight: 600 }}>
                          three or more axles
                        </strong>
                        , or a combination exceeding{' '}
                        <strong style={{ fontWeight: 600 }}>
                          26,000 pounds
                        </strong>
                        .
                      </span>
                    </li>

                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <data
                        itemProp="position"
                        value="4"
                        style={{ display: 'none' }}
                      />
                      <span itemProp="name">
                        <strong style={{ fontWeight: 600 }}>
                          Filing Frequency:
                        </strong>{' '}
                        IFTA tax returns are filed{' '}
                        <strong style={{ fontWeight: 600 }}>quarterly</strong>,
                        even when no miles are driven or fuel is purchased
                        during the reporting period.
                      </span>
                    </li>

                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <data
                        itemProp="position"
                        value="5"
                        style={{ display: 'none' }}
                      />
                      <span itemProp="name">
                        <strong style={{ fontWeight: 600 }}>
                          Filing Deadlines:
                        </strong>{' '}
                        Quarterly returns are generally due{' '}
                        <strong style={{ fontWeight: 600 }}>
                          April 30, July 31, October 31, and January 31.
                        </strong>
                      </span>
                    </li>
                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <data
                        itemProp="position"
                        value="6"
                        style={{ display: 'none' }}
                      />
                      <span itemProp="name">
                        <strong style={{ fontWeight: 600 }}>
                          IFTA License & Decals:
                        </strong>{' '}
                        Registered carriers receive an{' '}
                        <strong style={{ fontWeight: 600 }}>
                          IFTA license and two decals per qualified vehicle
                        </strong>
                        . The decals must generally be displayed on the
                        vehicle's cab.
                      </span>
                    </li>
                  </Box>

                  {/* Publisher needs an Organization with a logo ImageObject — no meta tags, same pattern */}
                  <div
                    itemProp="publisher"
                    itemScope
                    itemType="https://schema.org/Organization"
                    style={{ display: 'none' }}
                  >
                    <span itemProp="name">TaxZerone</span>
                    <div
                      itemProp="logo"
                      itemScope
                      itemType="https://schema.org/ImageObject"
                    >
                      <link
                        itemProp="url"
                        href="https://www.taxzerone.com/assets/images/logo.webp"
                      />
                      <data itemProp="width" value="380" />
                      <data itemProp="height" value="124" />
                    </div>
                  </div>
                </Box>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  If you’re a{' '}
                  <Link
                    href="/resources/excise-tax/form-2290-filing-guide-for-owner-operators/"
                    sx={{ color: '#0202ff' }}
                  >
                    {' '}
                    trucker
                  </Link>{' '}
                  or{' '}
                  <Link
                    href="/resources/excise-tax/form-2290-filing-guide-for-fleet-managers/"
                    sx={{ color: '#0202ff' }}
                  >
                    fleet owner
                  </Link>{' '}
                  operating in multiple states or Canadian provinces, you’ve
                  probably heard of IFTA. But what exactly is it, and why is it
                  essential for your business? This guide covers everything you
                  need to know about the International Fuel Tax Agreement
                  (IFTA)—from registration and reporting to decals, deadlines,
                  and penalties.
                </Typography>

                <Box
                  sx={{
                    pt: 0,
                  }}
                >
                  <Typography variant="body1" sx={{ mb: 0.5 }}>
                    <b>Table of Contents</b>
                  </Typography>
                  <List
                    sx={{ mb: 5, border: '1px solid #ccc', borderRadius: 2 }}
                  >
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#whatis" sx={{ color: '#0202ff' }}>
                          What is the International Fuel Tax Agreement (IFTA)?
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#who" sx={{ color: '#0202ff' }}>
                          Who needs to Register for IFTA?
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#what" sx={{ color: '#0202ff' }}>
                          What counts as a Qualified Motor Vehicle?
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#howto" sx={{ color: '#0202ff' }}>
                          How to Get an IFTA License and Decals?
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#how" sx={{ color: '#0202ff' }}>
                          How to Register IRP and IFTA?
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#filing" sx={{ color: '#0202ff' }}>
                          Filing Obligations & Deadlines
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#calc" sx={{ color: '#0202ff' }}>
                          Calculation of IFTA
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#pen" sx={{ color: '#0202ff' }}>
                          Penalties for Non-Compliance
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#between" sx={{ color: '#0202ff' }}>
                          Key difference between IFTA vs Form 2290
                        </Link>
                      </ListItemText>
                    </ListItem>

                    <ListItem
                      id="whatis"
                      sx={{ py: 0.5, alignItems: 'flex-start' }}
                    >
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#key" sx={{ color: '#0202ff' }}>
                          Key Benefits of IFTA
                        </Link>
                      </ListItemText>
                    </ListItem>
                  </List>
                </Box>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  What is the International Fuel Tax Agreement (IFTA)?
                </Typography>

                <Typography variant="body1" sx={{ mb: 3 }}>
                  The International Fuel Tax Agreement (IFTA) is a cooperative
                  agreement among 48 U.S. states and 10 Canadian provinces. It
                  simplifies fuel tax reporting for interstate commercial
                  carriers by allowing them to file a{' '}
                  <span style={{ fontWeight: 600 }} id="who">
                    single quarterly fuel tax return
                  </span>{' '}
                  through their{' '}
                  <span style={{ fontWeight: 600 }}>base jurisdiction</span>{' '}
                  (the state or province where the business is based).
                </Typography>

                {/* <Box
                  component={'div'}
                  sx={{ mb: 4, p: 2, borderRadius: 2 }}
                  className="gradient-bg"
                >
                  <Stack
                    direction={{ xs: 'column', md: 'row' }}
                    spacing={2}
                    justifyContent={'space-between'}
                    alignItems={'center'}
                  >
                    <Box sx={{ width: '100%' }}>
                      <Typography
                        variant="body1"
                        sx={{ fontWeight: 600, mb: 1 }}
                      >
                        Ready to File and Pay Your Taxes in an Easy Way?
                      </Typography>
                      <Typography variant="body2">
                        Use Electronic Funds Withdrawal (EFW) to file and pay in
                        one simple, secure step.
                      </Typography>
                    </Box>
                    <Box
                      sx={{ width: '100%', maxWidth: 200, textAlign: 'center' }}
                    >
                      <Button
                        variant="contained"
                        id="who"
                        sx={{
                          textTransform: 'none',
                          boxShadow: 'none',
                          borderRadius: 2,
                          backgroundColor: '#fff',
                          color: '#333',
                          '&:hover, &:focus, &:active': { color: '#fff' },
                        }}
                        size="large"
                        href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=efw`}
                      >
                        E-file Now
                      </Button>
                    </Box>
                  </Stack>
                </Box> */}
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 2,
                  }}
                >
                  Who needs to Register for IFTA?
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  You must register for IFTA if:
                </Typography>
                <Box component="ul" sx={{ mb: 3, '& li': { mb: 1.2 } }}>
                  <li>
                    Your business operates{' '}
                    <span style={{ fontWeight: 600 }}>
                      qualified motor vehicles in two or more IFTA
                      jurisdictions{' '}
                    </span>
                    (U.S. states or Canadian provinces).
                  </li>
                  <li id="what">
                    Your base jurisdiction (where your vehicles are registered
                    and where records are maintained) is part of the IFTA
                    agreement.
                  </li>
                </Box>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  If you operate only within one state (intrastate), you don’t
                  need to register for IFTA.{' '}
                </Typography>

                {/* <Alert
                  severity="warning"
                  id="eligible"
                  icon={false}
                  sx={{ mb: 3, mt: 0 }}
                >
                  <b>Note:</b> EFW is not available when filing a paper return.
                </Alert> */}

                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  What counts as a Qualified Motor Vehicle?
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Under IFTA, a{' '}
                  <span style={{ fontWeight: 600 }}>
                    qualified motor vehicle
                  </span>{' '}
                  is any vehicle used for transporting people or property and
                  that meets one of the following:
                </Typography>
                <Box component="ul" sx={{ mb: 3, '& li': { mb: 1.2 } }}>
                  <li>
                    Has{' '}
                    <span style={{ fontWeight: 600 }}>
                      two axles and a gross vehicle weight (GVW) over 26,000
                      pounds,
                    </span>{' '}
                    or
                  </li>
                  <li>
                    Has{' '}
                    <span style={{ fontWeight: 600 }}>three or more axles</span>
                    , regardless of weight, or
                  </li>
                  <li>
                    It is used in combination and the total weight of the
                    combination is over 26,000 pounds.{' '}
                  </li>
                </Box>
                <Alert
                  severity="warning"
                  id="howto"
                  icon={false}
                  sx={{ mb: 3, mt: 0, borderLeft: '3px solid rgb(102, 60, 0)' }}
                >
                  <b>Note:</b> Recreational vehicles and personal-use vehicles
                  are generally exempt from IFTA requirements.
                </Alert>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  How to Get an IFTA License and Decals?
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  To obtain an IFTA license:{' '}
                </Typography>
                <Box component="ol" sx={{ mb: 3, '& li': { mb: 1.2 } }}>
                  <li>
                    <span style={{ fontWeight: 600 }}>
                      Determine your base jurisdiction
                    </span>
                    . This is usually the state where your business is
                    registered and where you maintain vehicle records. or
                  </li>
                  <li>
                    <span style={{ fontWeight: 600 }}>
                      {' '}
                      Apply online or by mail
                    </span>{' '}
                    through your base state’s Department of Revenue or Motor
                    Carrier Services.
                  </li>
                  <li>
                    <span style={{ fontWeight: 600 }}>
                      {' '}
                      Pay the applicable fees
                    </span>
                    .
                  </li>
                  <li>
                    Receive your{' '}
                    <span style={{ fontWeight: 600 }}>IFTA license</span> and{' '}
                    <span style={{ fontWeight: 600 }}>two decals</span> per
                    qualified vehicle.{' '}
                  </li>
                  <li>Affix one decal on each side of the vehicle’s cab. </li>
                </Box>
                <Alert
                  severity="info"
                  id="filing"
                  icon={false}
                  sx={{ mb: 3, mt: 0, borderLeft: '3px solid #0288d1' }}
                >
                  <b>Important:</b> Your IFTA license must be always carried in
                  the vehicle while operating in IFTA jurisdictions.
                </Alert>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Filing Obligations & Deadlines
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Taxpayers must meet specific filing obligations based on their
                  income, business type, or vehicle usage.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{ fontWeight: 600, fontSize: '18px !important', mb: 1 }}
                >
                  Quarterly Filing Requirements:{' '}
                </Typography>
                <Typography variant="body1">
                  You are required to file an IFTA tax return every quarter,
                  even if:{' '}
                </Typography>
                <Box component="ul" sx={{ mb: 3, '& li': { mb: 1.2 } }}>
                  <li>No miles were driven, or </li>
                  <li>No fuel was purchased </li>
                </Box>
                <Typography
                  variant="h3"
                  sx={{ fontWeight: 600, fontSize: '18px !important', mb: 1 }}
                >
                  Due Dates:
                </Typography>
                <Typography variant="body1">
                  Deadlines vary by form but missing them can lead to penalties
                  and interest.
                </Typography>
                <Box sx={{ mb: 5 }}>
                  <TableContainer
                    sx={{ border: '1px solid #828282', borderRadius: 1 }}
                  >
                    <Table aria-label="EFW Individual">
                      <TableHead>
                        <TableRow sx={{ backgroundColor: '#ccc' }}>
                          <TableCell
                            sx={{
                              color: '#444',
                              fontWeight: 600,
                              width: 300,
                            }}
                          >
                            Quarter
                          </TableCell>
                          <TableCell
                            sx={{
                              color: '#444',
                              fontWeight: 600,
                              width: 300,
                            }}
                          >
                            Reporting Period
                          </TableCell>
                          <TableCell
                            sx={{
                              color: '#444',
                              fontWeight: 600,
                              width: 300,
                            }}
                          >
                            Due Date
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 600 }}>Q1</TableCell>
                          <TableCell>Jan – Mar</TableCell>
                          <TableCell>April 30</TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                          <TableCell sx={{ fontWeight: 600 }}>Q2</TableCell>
                          <TableCell>Apr – Jun</TableCell>
                          <TableCell>July 31</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 600 }}>Q3</TableCell>
                          <TableCell>Jul – Sep</TableCell>
                          <TableCell>October 31</TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                          <TableCell sx={{ fontWeight: 600 }}>Q4</TableCell>
                          <TableCell>Oct – Dec</TableCell>
                          <TableCell> January 31</TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
                <Typography variant="body1">You must report:</Typography>
                <Box component="ol" sx={{ mb: 3, '& li': { mb: 1.2 } }}>
                  <li>Miles traveled in each jurisdiction </li>
                  <li>Gallons of fuel purchased in each jurisdiction </li>
                  <li>Fuel used per jurisdiction </li>
                </Box>
                <Alert
                  severity="info"
                  id="calc"
                  icon={false}
                  sx={{ mb: 3, mt: 0, borderLeft: '3px solid #0288d1' }}
                >
                  <b>Important:</b> Keep all supporting documents for at least{' '}
                  <span style={{ fontWeight: 600 }}>four years</span> for audit
                  purposes. Calculation of IFTA
                </Alert>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Calculation of IFTA
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  The IFTA tax due is calculated based on:
                </Typography>
                <Box component="ol" sx={{ mb: 3, '& li': { mb: 1.2 } }}>
                  <li>
                    {' '}
                    <span style={{ fontWeight: 600 }}>
                      {' '}
                      Total miles traveled
                    </span>{' '}
                    in each IFTA jurisdiction{' '}
                  </li>
                  <li>
                    <span style={{ fontWeight: 600 }}>
                      Total gallons of fuel purchased{' '}
                    </span>{' '}
                  </li>
                  <li>
                    {' '}
                    <span style={{ fontWeight: 600 }}>
                      {' '}
                      Average miles per gallon (MPG)
                    </span>{' '}
                    of your vehicles{' '}
                  </li>
                </Box>
                <Box
                  sx={{
                    mb: 3,
                    backgroundColor: '#dfe7fd',
                    p: 2,
                    borderRadius: 3,

                    border: '2px solid #3e66d4ff',
                  }}
                >
                  <Typography
                    variant="h3"
                    sx={{
                      fontWeight: 600,
                      mt: 1,
                      fontStyle: 'italic',
                      mb: 0.8,
                      fontSize: '18px !important',
                    }}
                  >
                    Basic Formula:
                  </Typography>
                  <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                    (Total Miles ÷ Total Gallons) = MPG
                  </Typography>
                  <Typography variant="body1" sx={{ mb: 1 }}>
                    Then, for each jurisdiction:
                  </Typography>
                  <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                    (Miles in jurisdiction ÷ MPG) × Tax rate = Tax owed
                  </Typography>

                  <Typography variant="body1" id="pen" sx={{ mb: 1 }}>
                    Your base jurisdiction will calculate your total fuel tax
                    owed or any refund due and distribute funds accordingly.
                  </Typography>
                </Box>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Penalties for Non-Compliance
                </Typography>
                <Typography variant="body1">
                  Failure to meet IFTA requirements can result in the following
                  penalties:
                </Typography>
                <Box component="ul" sx={{ mb: 3, '& li': { mb: 1.2 } }}>
                  <li>
                    {' '}
                    <span style={{ fontWeight: 600 }}>
                      {' '}
                      Late Filing Penalty:
                    </span>{' '}
                    $50 or 10% of the total tax due—whichever is greater.
                  </li>
                  <li>
                    <span style={{ fontWeight: 600 }}>Interest Charges:</span>{' '}
                    1% per month on the amount of tax owed to each jurisdiction
                    until paid.
                  </li>
                  <li>
                    {' '}
                    <span style={{ fontWeight: 600 }}>
                      {' '}
                      License Suspension or Revocation:
                    </span>{' '}
                    Continued non-compliance may result in your IFTA license
                    being suspended or revoked, preventing you from operating
                    across state lines.
                  </li>
                  <li>
                    <span style={{ fontWeight: 600 }}>Audit Risk:</span> Poor
                    recordkeeping or incorrect reporting may trigger an audit.
                  </li>
                  <li>
                    <span style={{ fontWeight: 600 }}>
                      Additional Fines and Back Taxes:
                    </span>{' '}
                    If an audit reveals errors or underreporting, you may be
                    required to pay additional taxes, interest, and penalties.
                  </li>
                  <li>
                    <span style={{ fontWeight: 600 }}>
                      Recordkeeping Requirement:
                    </span>{' '}
                    You must maintain accurate fuel and mileage records for at
                    least <span style={{ fontWeight: 600 }}>four years</span> to
                    stay compliant.
                  </li>
                </Box>
                <Alert
                  severity="info"
                  id="between"
                  icon={false}
                  sx={{ mb: 3, mt: 0, borderLeft: '3px solid #0288d1' }}
                >
                  <b>To avoid these penalties: </b>File on time, pay your taxes
                  in full, and keep detailed, accurate records.
                </Alert>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Key difference between IFTA vs Form 2290
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  While both IFTA and{' '}
                  <Link
                    href="/excise-tax/efile-irs-form-2290-heavy-vehicle-use-tax-online/"
                    sx={{ color: '#0202ff' }}
                  >
                    {' '}
                    Form 2290
                  </Link>{' '}
                  apply to heavy vehicles, they serve{' '}
                  <span style={{ fontWeight: 600 }}>
                    different tax purposes
                  </span>{' '}
                  and are administered by{' '}
                  <span style={{ fontWeight: 600 }}>different agencies</span>
                  .{' '}
                </Typography>

                <Box sx={{ mb: 5 }}>
                  <TableContainer
                    sx={{ border: '1px solid #828282', borderRadius: 1 }}
                  >
                    <Table aria-label="EFW Individual">
                      <TableHead>
                        <TableRow sx={{ backgroundColor: '#ccc' }}>
                          <TableCell
                            sx={{
                              color: '#444',
                              fontWeight: 600,
                              width: 300,
                            }}
                          >
                            Aspect
                          </TableCell>
                          <TableCell
                            sx={{
                              color: '#444',
                              fontWeight: 600,
                              width: 400,
                            }}
                          >
                            IFTA
                          </TableCell>
                          <TableCell
                            sx={{
                              color: '#444',
                              fontWeight: 600,
                              width: 400,
                            }}
                          >
                            Form 2290
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 600 }}>
                            Full Name
                          </TableCell>
                          <TableCell>
                            International Fuel Tax Agreement
                          </TableCell>
                          <TableCell>
                            Heavy Highway Vehicle Use Tax Return
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                          <TableCell sx={{ fontWeight: 600 }}>
                            Purpose
                          </TableCell>
                          <TableCell>
                            To report and pay fuel use taxes across
                            jurisdictions
                          </TableCell>
                          <TableCell>
                            To pay the federal tax for using highways with heavy
                            vehicles
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 600 }}>
                            Who Requires It?
                          </TableCell>
                          <TableCell>
                            Carriers operating qualified motor vehicles in 2+
                            states or provinces
                          </TableCell>
                          <TableCell>
                            Anyone operating a highway vehicle with a taxable
                            gross weight of 55,000 lbs or more
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                          <TableCell sx={{ fontWeight: 600 }}>
                            Filing Frequency
                          </TableCell>
                          <TableCell>Quarterly</TableCell>
                          <TableCell> Annually</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 600 }}>
                            Administered By
                          </TableCell>
                          <TableCell>
                            State agencies (your base jurisdiction){' '}
                          </TableCell>
                          <TableCell>Internal Revenue Service (IRS) </TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                          <TableCell sx={{ fontWeight: 600 }}>
                            Payment Based On
                          </TableCell>
                          <TableCell>
                            Fuel usage and miles traveled per jurisdiction
                          </TableCell>
                          <TableCell>
                            {' '}
                            Vehicle weight and month of first use
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 600 }}>
                            Proof of Filing Needed
                          </TableCell>
                          <TableCell>IFTA license and decals</TableCell>
                          <TableCell>
                            Stamped Schedule 1 (proof of payment)
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                          <TableCell sx={{ fontWeight: 600 }}>
                            Renewal
                          </TableCell>
                          <TableCell>
                            Annual renewal for license and decals
                          </TableCell>
                          <TableCell>
                            {' '}
                            Annual filing based on vehicle&apos;s first month of
                            use
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
                <Typography variant="body1">
                  <span style={{ fontWeight: 600 }}> 💡Tip:</span> If you own a
                  heavy vehicle? You must{' '}
                  <Link
                    href="/excise-tax/efile-irs-form-2290-heavy-vehicle-use-tax-online/"
                    sx={{ color: '#0202ff' }}
                  >
                    {' '}
                    file Form 2290
                  </Link>{' '}
                </Typography>
                <Box component="ul" sx={{ mb: 3, '& li': { mb: 1.2 } }}>
                  <li>
                    If your truck weighs 55,000 lbs or more, the IRS requires
                    you to file Form 2290 and pay the Heavy Vehicle Use Tax
                    (HVUT) every year.
                  </li>
                  <li>
                    Don’t risk delays with IRP or IFTA registration—proof of
                    payment (Schedule 1) is mandatory!
                  </li>
                  <ul>
                    <li>
                      {' '}
                      <span style={{ fontWeight: 600 }}> Need help?</span>{' '}
                      <Link
                        href="/resources/excise-tax/form-2290-instructions/"
                        sx={{ color: '#0202ff' }}
                      >
                        Check out Form 2290 Instructions
                      </Link>
                    </li>
                    <li>
                      <span style={{ fontWeight: 600 }}>Watch:</span>{' '}
                      <Link
                        href="https://www.youtube.com/watch?v=QnUmGYD2yLw&list=PLmvRKAW8mnOiHgODlf35zeTg7wBnQ1SZg&index=2&pp=iAQB"
                        target="_blank"
                        id="key"
                        sx={{ color: '#0202ff' }}
                      >
                        How to File Form 2290 within Minutes?
                      </Link>{' '}
                    </li>
                    <li>
                      <span style={{ fontWeight: 600 }}>Any Corrections:</span>{' '}
                      <Link
                        href="/resources/excise-tax/form-2290-amendments/"
                        sx={{ color: '#0202ff' }}
                      >
                        Form 2290 Amendments
                      </Link>{' '}
                    </li>
                  </ul>
                </Box>

                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Key Benefits of IFTA
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  The International Fuel Tax Agreement (IFTA) offers significant
                  advantages for commercial motor carriers operating across
                  multiple states and provinces. By simplifying fuel tax
                  reporting and compliance, IFTA helps trucking companies and
                  owner-operators save time, reduce costs, and avoid penalties.
                  Here are the key benefits:{' '}
                </Typography>
                <Box component="ul" sx={{ mb: 3, '& li': { mb: 1.2 } }}>
                  <li>Simplified Fuel Tax Reporting</li>
                  <li>Reduced Administrative Burden</li>
                  <li>Fair Allocation of Fuel Taxes</li>
                  <li>Uniform Rules and Procedures</li>
                  <li>Enhanced Compliance and Enforcement </li>
                  <li>Cost Savings </li>
                  <li>Convenient IFTA Decal System </li>
                </Box>

                <Alert severity="info" icon={false} sx={{ borderRadius: 2 }}>
                  <Accordion
                    // defaultExpanded
                    sx={{
                      '&.MuiAccordion-root::before, &.MuiAccordion-root.Mui-expanded::before':
                        {
                          background: 'none !important',
                        },
                      '&.MuiAccordion-root': { background: 'none !important' },
                    }}
                  >
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon />}
                      aria-controls=""
                      id=""
                      sx={{
                        px: 0,
                        m: 0,
                        justifyContent: 'flex-start',
                        '& .MuiAccordionSummary-content, & .MuiAccordionSummary-content.Mui-expanded':
                          { m: '0 !important', flexGrow: 0 },
                      }}
                    >
                      <Typography
                        variant="body1"
                        sx={{
                          fontWeight: 600,
                          color: '#014361',
                          fontSize: 16,
                        }}
                      >
                        Sources and References
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails sx={{ p: 0 }}>
                      <Typography
                        component="div"
                        variant="body2"
                        sx={{ mt: 1 }}
                      >
                        The above information is referenced from the official
                        IFTA organization. For more details, please refer to{' '}
                        <Link
                          href="https://www.iftach.org/"
                          target="_blank"
                          sx={{ color: '#0202ff' }}
                        >
                          IFTA, Inc.
                        </Link>{' '}
                        Website.
                      </Typography>
                    </AccordionDetails>
                  </Accordion>
                </Alert>
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Grid>

      <Container maxWidth="lg" sx={{ mb: 5, my: { xs: 6, sm: 6, md: 6 } }}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={{ xs: 2, md: 5 }}
        >
          <Box
            sx={{
              width: '100%',
              textAlign: 'center',
            }}
          >
            <Typography
              variant="h2"
              sx={{
                fontSize: { lg: 34, md: 30, sm: 26, xs: 22 },
                fontWeight: 600,
                mb: 3,
                textAlign: 'center',
                lineHeight: 1.5,
              }}
            >
              Simplify IFTA Compliance and Stay Ahead of Deadlines!
            </Typography>

            <Typography
              variant="body1"
              sx={{
                fontSize: { lg: 18, md: 18, sm: 16, xs: 16 },
                fontWeight: 400,
                color: '#333333',
                mb: 4,
              }}
            >
              With a clear understanding of IFTA requirements and the right
              support, you can file accurately, <br /> avoid costly penalties,
              and keep your fleet compliant year-round.
            </Typography>

            <Box sx={{ textAlign: { xs: 'center', md: 'center' } }}>
              <Button
                variant="contained"
                sx={{
                  mt: 1,
                  transition: 'all 0.3s ease 0s',
                  '&:hover, &:focus, &:active': { transform: 'scale(1.1)' },
                }}
                className="btn-primary"
                href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=ifta`}
              >
                Start Filing Now
              </Button>
            </Box>
          </Box>
        </Stack>
      </Container>
    </AppLayout>
  );
};

export default FuelTax;
